import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import IconOrientationSectionalLeft from 'components/icons/iconOrientationSectionalLeft';
import IconOrientationSectionalRight from 'components/icons/iconOrientationSectionalRight';
import IconOrientationArmChairLeft from 'components/icons/iconOrientationArmChairLeft';
import IconOrientationArmChairRight from 'components/icons/iconOrientationArmChairRight';
import IconOrientationArmChaiseLeft from 'components/icons/iconOrientationArmChaiseLeft';
import IconOrientationArmChaiseRight from 'components/icons/iconOrientationArmChaiseRight';
import IconOrientationChaiseArmFacingLeft from 'components/icons/iconOrientationChaiseArmFacingLeft';
import IconOrientationChaiseArmFacingRight from 'components/icons/iconOrientationChaiseArmFacingRight';
import Placeholder from 'components/consumer/Placeholder';
import JBImage from 'components/essentials/JBImage';
import IconTick from 'components/icons/iconTick';
import useScreen from 'hooks/useScreen';

export const findOrientationIcon = (optionName, optionSku, pathname, slug) => {
  let image = '';
  let customItem;
  switch (optionName) {
    case 'Bun Legs':
      image =
        'https://joybird2.imgix.net/static-v1/product-options/legs-bun.jpg';
      break;
    case 'Caster Legs':
      image =
        'https://joybird2.imgix.net/static-v1/product-options/legs-caster.jpg';
      break;
    case 'Not included':
      image =
        'https://joybird2.imgix.net/static-v1/product-options/no-cushion%402x.jpg';
      break;
    case 'Included':
      image =
        'https://joybird2.imgix.net/static-v1/product-options/extra-cushion@2x.jpg';
      break;
    case 'Right':
      if (
        optionSku === '-RAF' &&
        (pathname.includes('daybeds-chaises') ||
          pathname.includes('apartment-desks'))
      ) {
        customItem = <IconOrientationChaiseArmFacingRight />;
      } else if (
        optionSku === '-SCHR' ||
        (optionSku === '-RAF' && slug.includes('louie-chair'))
      ) {
        customItem = <IconOrientationArmChairRight />;
      } else if (
        optionSku === '-SCHSR' ||
        optionSku === '-SACAFR' ||
        (optionSku === '-RAF' && !slug.includes('sectional')) ||
        (optionSku === '-RFB' && !slug.includes('sectional'))
      ) {
        customItem = <IconOrientationArmChaiseRight />;
      } else {
        customItem = <IconOrientationSectionalRight />;
      }
      break;
    case 'Left':
      if (
        optionSku === '-LAF' &&
        (pathname.includes('daybeds-chaises') ||
          pathname.includes('apartment-desks'))
      ) {
        customItem = <IconOrientationChaiseArmFacingLeft />;
      } else if (
        optionSku === '-SCHL' ||
        (optionSku === '-LAF' && slug.includes('louie-chair'))
      ) {
        customItem = <IconOrientationArmChairLeft />;
      } else if (
        optionSku === '-SCHSL' ||
        optionSku === '-SACAFL' ||
        (optionSku === '-LAF' && !slug.includes('sectional')) ||
        (optionSku === '-LFB' && !slug.includes('sectional'))
      ) {
        customItem = <IconOrientationArmChaiseLeft />;
      } else {
        customItem = <IconOrientationSectionalLeft />;
      }
      break;
    default:
      image = undefined;
  }

  return { image, customItem };
};

const getPathSlug = location => {
  const params = location.pathname.split('/').filter(v => !!v);
  return params[params.length - 1] || '';
};

const PDPImageAndTextOption = ({
  loading,
  optionValue = {},
  selectedId,
  onOptionValueSelection,
  variant = 'regular',
  toggleFlyOut,
}) => {

  const { isMobile } = useScreen();
  const location = useLocation();
  const pathSlug = getPathSlug(location);

  const optionValueId = optionValue.id;
  const { isDisabled } = optionValue;
  const { content = {} } = optionValue;
  const imgProps = variant === 'regular' ? { height: '58' } : { sizes: '25vw' };
  const isSelected = optionValueId === selectedId;
  const { image = null, customItem = null } = findOrientationIcon(
    optionValue.value?.trim(),
    optionValue.sku,
    location.pathname,
    pathSlug
  );
  return (
    <Placeholder
      type="rect"
      loading={loading}
      className="min-w-[150px] min-h-[60px] flex lg:min-w-[143px]"
    >
      <div
        role="radio"
        tabIndex={0}
        className={classNames(
          'bg-white justify-start min-h-[58px] min-w-[144px] flex flex-row items-center relative rounded p-px mt-0 mr-3 mb-3 ml-0 border border-solid lg:max-w-[142px] lg:min-w-[142px] hover:cursor-pointer',
          '[&:nth-of-type(3n+0)]:lg:mr-0',
          {
            'border-gray-light6 hover:border-gray-light2': !isSelected,
            'border-white hover:border-white shadow-[inset_0_0_0_1px_#262626]': isSelected,
            '!shadow-[inset_0_0_0_1px_#262626,inset_0_0_0_3px_#fff] hover:!cursor-not-allowed': isDisabled,
          }
        )}
        onClick={() => {
          if (isDisabled) {
            return;
          }
          if (isMobile) {
            toggleFlyOut(true, optionValueId);
          }
          onOptionValueSelection(optionValueId);
        }}
        onMouseEnter={
          !isMobile ? () => toggleFlyOut(true, optionValueId) : null
        }
        onMouseLeave={
          !isMobile ? () => toggleFlyOut(false, optionValueId) : null
        }
        aria-checked={isSelected}
      >
        {content.image && variant !== 'textOnly' ? (
          <div
            className="flex-1 h-[58px] w-[58px] [&_svg]:h-11 [&_svg]:w-11 [&_svg]:m-2"
            key={content.image}
          >
            {!customItem ? (
              <JBImage src={image || content.image} {...imgProps} />
            ) : (
              customItem
            )}
          </div>
        ) : null}
        {content.text ? (
          <div
            className={classNames(
              'text-sm flex-1 font-bold text-gray ml-3 mr-2',
              {
                'text-center':
                  variant === 'textOnly' ||
                  (!image && !content?.image && !customItem),
              }
            )}
            key={content.text}
          >
            {content.text}
          </div>
        ) : null}
        {isSelected && (
          <div
            className={classNames('z-[1]', {
              'items-center bg-gray-dark flex h-4 justify-center absolute right-[calc(1rem_-_15px)] top-[calc(1rem_-_15px)] w-4 rounded-none': isSelected,
              '[&_svg]:h-2.5 [&_svg]:w-2.5': isSelected,
              '[&_svg_path]:fill-white [&_svg_path]:stroke-white': isSelected,
            })}
          >
            <IconTick height="22px" width="22px" />
          </div>
        )}
        {isDisabled ? (
          <>
            <div
              className={classNames({
                'absolute left-0 top-0 bg-[hsla(0,0%,100%,.4)] w-full h-full z-50': isDisabled,
              })}
            />
            <div
              className={classNames({
                'absolute left-0 top-0 w-full h-full [background:linear-gradient(to_top_left,transparent_0,transparent_calc(50%_-_.8px)_,#000_50%,transparent_calc(50%_+_.8px)_,transparent)] z-[51] rounded-lg': isDisabled,
              })}
            />
          </>
        ) : null}
      </div>
    </Placeholder>
  );
};

PDPImageAndTextOption.propTypes = {
  content: PropTypes.shape({
    image: PropTypes.string,
    text: PropTypes.string,
  }),
  loading: PropTypes.bool,
  onOptionClick: PropTypes.func,
  onOptionValueSelection: PropTypes.func,
  optionId: PropTypes.number,
  optionValue: PropTypes.object,
  optionValueId: PropTypes.number,
  selectedId: PropTypes.number,
  toggleFlyOut: PropTypes.func,
  variant: PropTypes.oneOf(['regular', 'textOnly']),
};

export default PDPImageAndTextOption;
